import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { logErrors } from 'utils';
import {
  fetchPlanChangeEligibility,
  getAdditionalPiiDetails,
  submitPlanChange,
} from 'services/plan/planAsync';
import { setToastAlert } from 'services/toast-alert/toastAlertSlice';
import { selectUserAddress, selectUserDob } from 'services/user/userSelectors';
import {
  selectIsPlanUpgradeSubmitted,
  selectIsUnlockInsuranceEligible,
  selectUnlockInsuranceSurface,
  selectUnlockInsuranceTargetOfferCode,
  selectUnlockUpgradeProduct,
} from '../services/unlockInsuranceSelectors';
import {
  resetUnlockInsuranceSurface,
  setIsPlanUpgradeSubmitted,
  setIsUnlockInsuranceModalClosed,
  setUnlockInsuranceSurface,
  setUnlockInsuranceTargetOfferCode,
} from '../services/unlockInsuranceSlice';
import { UnlockInsuranceFlowMode } from '../services/types';

const ERROR_MESSAGE =
  'Something went wrong and your plan was not updated. Please try again.';

function useUnlockInsuranceFlow() {
  const dispatch = useAppDispatch();
  const isEligible = useAppSelector(selectIsUnlockInsuranceEligible);
  const isPlanUpgradeSubmitted = useAppSelector(selectIsPlanUpgradeSubmitted);
  const upgradeProduct = useAppSelector(selectUnlockUpgradeProduct);
  const birthDate = useAppSelector(selectUserDob);
  const address = useAppSelector(selectUserAddress);
  /* Surface lives in the slice: every host that could trigger the flow is behind an
     eligibility gate that the flow itself flips, so local state would be unmounted mid-flow. */
  const surface = useAppSelector(selectUnlockInsuranceSurface);
  const targetOfferCode = useAppSelector(selectUnlockInsuranceTargetOfferCode);

  /* Ref guards re-entrancy synchronously; state drives the CTA's disabled prop. */
  const [isInFlight, setIsInFlight] = useState(false);
  const inFlightRef = useRef(false);
  const pendingClickRef = useRef(false);

  const latchOnLoad = useCallback(
    (mode: UnlockInsuranceFlowMode) => {
      if (mode === 'onLoad') dispatch(setIsUnlockInsuranceModalClosed(true));
    },
    [dispatch],
  );

  /** Fires the PUT, then refreshes eligibility so the store stops reporting the old plan. */
  const submitPlanUpgrade = useCallback(async () => {
    const offerCode = targetOfferCode ?? upgradeProduct?.offer.code;
    if (!offerCode) return false;
    await dispatch(submitPlanChange(offerCode));
    dispatch(setIsPlanUpgradeSubmitted(true));
    /* Best-effort: the upgrade already succeeded, so a failed refresh must not surface as one. */
    try {
      await dispatch(fetchPlanChangeEligibility());
    } catch (error) {
      logErrors(error);
    }
    return true;
  }, [dispatch, targetOfferCode, upgradeProduct]);

  /**
   * Resolves to whether a surface was opened. `false` means the caller still owns whatever
   * comes next — the SessionModalSequence entry has to advance the queue itself, or the
   * sequence stalls on a flow that finished without rendering anything.
   */
  const runFlow = useCallback(
    async (mode: UnlockInsuranceFlowMode): Promise<boolean> => {
      if (inFlightRef.current || isPlanUpgradeSubmitted) return false;

      /* Another entry point already owns the screen. Latch, so the queue entry retires instead
         of being re-pushed on every recompute — which would re-prompt on close, or stall it. */
      if (surface !== 'idle') {
        latchOnLoad(mode);
        return false;
      }

      inFlightRef.current = true;
      setIsInFlight(true);

      try {
        /* No matching product: stop before any request, and reuse the same dialog copy. */
        if (!upgradeProduct) {
          latchOnLoad(mode);
          if (mode === 'onClick') {
            dispatch(setUnlockInsuranceSurface('confirmation'));
            return true;
          }
          return false;
        }

        /* Angular captures the offer code before deciding to show the form
           (unlock-insurance.component.ts:81), so the submit can never fail to resolve one. */
        dispatch(setUnlockInsuranceTargetOfferCode(upgradeProduct.offer.code));

        const details = await dispatch(
          getAdditionalPiiDetails(upgradeProduct.plan.code),
        );

        if (details?.additionalPiiRequired && (!birthDate || !address?.state)) {
          dispatch(setUnlockInsuranceSurface('modal'));
          latchOnLoad(mode);
          return true;
        }

        await submitPlanUpgrade();
        latchOnLoad(mode);
        if (mode === 'onClick') {
          dispatch(setUnlockInsuranceSurface('confirmation'));
          return true;
        }
        return false;
      } catch (error) {
        logErrors(error);
        dispatch(setToastAlert({ status: 'error', message: ERROR_MESSAGE }));
        latchOnLoad(mode);
        return false;
      } finally {
        inFlightRef.current = false;
        setIsInFlight(false);
      }
    },
    [
      address,
      birthDate,
      dispatch,
      isPlanUpgradeSubmitted,
      latchOnLoad,
      submitPlanUpgrade,
      surface,
      upgradeProduct,
    ],
  );

  /* A click before the reads land queues rather than no-ops. */
  useEffect(() => {
    if (!isEligible || !pendingClickRef.current) return;
    pendingClickRef.current = false;
    runFlow('onClick');
  }, [isEligible, runFlow]);

  const triggerOnLoad = useCallback(() => runFlow('onLoad'), [runFlow]);

  const triggerOnClick = useCallback(() => {
    if (!isEligible) {
      pendingClickRef.current = true;
      return;
    }
    runFlow('onClick');
  }, [isEligible, runFlow]);

  /**
   * Offers & Upgrades entry (BR-009/§2.6): opens the PII modal unconditionally — no
   * additionalPiiRequired call, no PII pre-check — and never latches the session flag.
   */
  const triggerPlanCard = useCallback(
    (offerCode: string) => {
      if (isPlanUpgradeSubmitted || surface !== 'idle') return;
      dispatch(setUnlockInsuranceTargetOfferCode(offerCode));
      dispatch(setUnlockInsuranceSurface('modal'));
    },
    [dispatch, isPlanUpgradeSubmitted, surface],
  );

  /** Called by the modal once the PII PATCH succeeds; the PUT must not wait on the card. */
  const handlePiiSuccess = useCallback(async () => {
    try {
      const submitted = await submitPlanUpgrade();
      dispatch(
        submitted
          ? setUnlockInsuranceSurface('success')
          : resetUnlockInsuranceSurface(),
      );
    } catch (error) {
      logErrors(error);
      dispatch(setToastAlert({ status: 'error', message: ERROR_MESSAGE }));
      dispatch(resetUnlockInsuranceSurface());
    }
  }, [dispatch, submitPlanUpgrade]);

  const closeSurface = useCallback(() => {
    dispatch(resetUnlockInsuranceSurface());
  }, [dispatch]);

  return {
    triggerOnLoad,
    triggerOnClick,
    triggerPlanCard,
    handlePiiSuccess,
    closeSurface,
    isInFlight,
  };
}

export default useUnlockInsuranceFlow;
